/**
 * "Get out: custom amount" (under the card's Details; docs/market-making-ux.md §3 + Revision 2,
 * where the main UI's one-step Get out lives in GetOut.tsx). Defaults to the contract difference
 * on the short side, and the user can change the contracts, the venue and the order type. It buys
 * the short side, at the best current price across the allowed venues (Real: connected venues only), so
 * the opportunity pays about the same whichever side wins. greenbook only suggests it — the user
 * edits the venue, price and order type and presses the button.
 *
 * One order-sheet sheet (useMmSheet.ts) with the opportunity id and its model (none on a quote
 * from before models), so the hedge joins the same opportunity; sized in contracts (`{ shares }`). The numbers are the sheet's (exact once its
 * preflight answers), and the per-outcome result is computed from them. Paper places on the
 * press; Real reviews first (the sheet's `confirm_required`, listed in the red confirm) and the
 * Confirm sends `confirm()`. Either re-reads the sheet just before sending: moved numbers are
 * shown instead of sent. By default the long side's resting bids are cancelled first (otherwise
 * they keep adding exposure).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { LiquidityPosition, PartnerLiquidity } from "@greenbook/client";
import type { MoneyMode, OrderSheetClient, OrderType, SheetInit } from "@openmarketsai/order-sheet";
import {
  clampCents,
  defaultBidCents,
  legRefsFrom,
  placedLegs,
  sheetPreview,
  sheetShares,
  type HedgeNeed,
  type MmEntry,
  type MmLeg,
  type OppBook,
  type PlacedLeg,
} from "../../lib/marketMaking.js";
import { isActionable } from "../../lib/marketsBoard.js";
import { issueText } from "../../lib/ticketModel.js";
import { fmtPrice } from "../../lib/format.js";
import { useOddsFormat } from "../../lib/odds.js";
import type { Ccy } from "../../lib/portfolio.js";
import { Pill } from "../kit.js";
import {
  btnBase,
  btnIdle,
  btnPrimary,
  CentsStepper,
  ConfirmOrders,
  fmtContracts,
  modeOf,
  money,
  signedMoney,
  toneOf,
  type MarketMakingSource,
  type OrderAccess,
} from "./shared.js";
import { costText, sheetRef } from "./GetOut.js";
import { useMmSheet } from "./useMmSheet.js";

export function HedgePanel({
  source,
  entry,
  book,
  need,
  shortPosition,
  ccy,
  mode,
  onPlaced,
  onClose,
}: {
  source: MarketMakingSource;
  entry: MmEntry;
  book: OppBook;
  need: HedgeNeed;
  /** The short side's current liquidity (undefined while prices load). */
  shortPosition: LiquidityPosition | undefined;
  ccy: Ccy;
  mode: MoneyMode;
  onPlaced: (legs: PlacedLeg[]) => void;
  onClose: () => void;
}) {
  const orders = source.useOrders();
  const { setMode } = source.useMode();
  // The sheet trades in the opportunity's own money.
  const sheetMode = modeOf(entry.ccy);
  const scope = source.useScope(sheetMode);
  const short = entry.sides[need.short];
  const mismatch = (entry.ccy === "USD") !== (mode === "real");

  // Venues quoting the short side now, cheapest first (Real: connected venues only).
  const venues = useMemo(() => {
    const pls = (shortPosition?.partner_liquidities ?? []).filter(
      (pl) => isActionable(pl) && (sheetMode !== "real" || !scope.allowed || scope.allowed.has(pl.partner_id)),
    ) as PartnerLiquidity[];
    return pls.sort((a, b) => (a.price as number) - (b.price as number));
  }, [shortPosition, sheetMode, scope.allowed]);

  return (
    <div className="gb-fade mt-3 flex flex-col gap-3 rounded-md border border-amber/40 bg-amber/[0.04] p-3">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-amber">Get out: custom amount</span>
        <button type="button" onClick={onClose} className="ml-auto text-[12px] text-faint hover:text-ink" aria-label="Close">
          ✕
        </button>
      </div>

      {mismatch && (
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-amber">
          <span className="min-w-0 flex-1">
            This opportunity is {entry.ccy === "ATLAS" ? "Paper" : "Real"}; your trading mode is {mode === "real" ? "Real" : "Paper"}. New orders have to be in the same money.
          </span>
          <button type="button" onClick={() => setMode(entry.ccy === "ATLAS" ? "paper" : "real")} className={`${btnBase} ${btnPrimary}`}>
            Switch to {entry.ccy === "ATLAS" ? "Paper" : "Real"}
          </button>
        </div>
      )}

      {!shortPosition ? (
        <div className="text-[12px] text-dim">Loading {short.label} prices…</div>
      ) : venues.length === 0 ? (
        <div className="text-[12px] text-dim">
          No {sheetMode === "real" ? "connected " : ""}venue is quoting {short.label} right now, so there's nothing to buy.
        </div>
      ) : !orders.om || orders.client !== "ok" ? (
        <p className="text-[12px] text-amber">
          {orders.client === "signed_out" ? "Sign in with OpenMarkets to place orders." : "Placing orders needs a Sign in with OpenMarkets session (a pasted API key can't place orders)."}
        </p>
      ) : (
        <HedgeSheet
          om={orders.om}
          orders={orders}
          source={source}
          entry={entry}
          book={book}
          need={need}
          venues={venues}
          ccy={ccy}
          mismatch={mismatch}
          onPlaced={onPlaced}
          onClose={onClose}
        />
      )}
    </div>
  );
}

function HedgeSheet({
  om,
  orders,
  source,
  entry,
  book,
  need,
  venues,
  ccy,
  mismatch,
  onPlaced,
  onClose,
}: {
  om: OrderSheetClient;
  orders: OrderAccess;
  source: MarketMakingSource;
  entry: MmEntry;
  book: OppBook;
  need: HedgeNeed;
  venues: PartnerLiquidity[];
  ccy: Ccy;
  mismatch: boolean;
  onPlaced: (legs: PlacedLeg[]) => void;
  onClose: () => void;
}) {
  const { format } = useOddsFormat();
  const { wantsReal } = source.useMode();
  const sheetMode = modeOf(entry.ccy);
  const short = entry.sides[need.short];
  const long = entry.sides[need.long];

  // The sheet opens on the cheapest venue; picking another venue selects it on the same sheet.
  const [first] = useState(() => venues[0]);
  const [ref] = useState(() => sheetRef(first.liquidity_hash ?? null, short.positionHash, first.partner_id));
  const missing = Math.round(need.contracts * 100) / 100;
  const init: SheetInit = {
    mode: sheetMode,
    orderType: "market",
    partnerId: first.partner_id,
    ...(sheetShares(need.contracts) != null ? { stake: { shares: sheetShares(need.contracts)! } } : {}),
    slippage: 0.01,
    opportunityId: entry.opportunityId,
    // The opportunity's own model, or none when it has none (else 400 model_mismatch).
    ...(entry.modelId ? { modelId: entry.modelId } : {}),
  };
  const s = useMmSheet(om, ref, init, orders.canTradeReal);
  const { h } = s;
  const f = s.figures;
  const venueId = h.venue?.partnerId ?? first.partner_id;
  const venue = venues.find((v) => v.partner_id === venueId);
  const venueName = h.venue?.name ?? venue?.partner_name ?? venueId;

  // Limits: the venue's served order types in Real, the practice wallet's in Paper.
  const limitOk =
    sheetMode === "paper"
      ? (h.account?.practiceWallet.orderTypes ?? ["market", "limit"]).includes("limit")
      : !!h.venue?.supports.orderTypes.includes("limit");
  const type: OrderType = h.orderType;
  useEffect(() => {
    if (!h.loading && type === "limit" && !limitOk) h.set({ orderType: "market", maxPrice: null });
  }, [h, type, limitOk]);
  const cents = h.orderType === "limit" && h.maxPrice != null ? Math.round(h.maxPrice * 100) : (defaultBidCents(h.venue?.price ?? venue?.price) ?? 50);

  const [qtyText, setQtyText] = useState(() => String(missing));
  const setQty = (text: string) => {
    setQtyText(text);
    const n = Number(text.replace(/[,\s]/g, ""));
    const shares = Number.isFinite(n) && n > 0 && n <= 100_000 ? sheetShares(n) : null;
    h.set({ stake: shares == null ? null : { shares } });
  };
  const qtyBad = h.stake.value == null;
  const preview = f ? sheetPreview(book, need.short, f) : null;

  const longResting = useMemo(
    () => book.sides[need.long].legs.filter((l) => book.resting.includes(l)).map((l: MmLeg) => l.router_order_id),
    [book, need.long],
  );
  const shortResting = book.sides[need.short].legs.filter((l) => book.resting.includes(l)).length;
  const [cancelLong, setCancelLong] = useState(true);
  const cancels = cancelLong ? longResting.length : 0;
  const cancelled = useRef(new Set<string>());

  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<{ tone: "moved" | "error"; text: string } | null>(null);
  const realBlocked = wantsReal && !orders.canTradeReal;
  const busy = s.step != null;
  const issues = !mismatch && !h.loading && !h.validation.ok ? issueText(h.validation.blocking, h, format, orders.canTradeReal) : null;
  const canPlace = !!f && !busy && !s.checking && !mismatch && !realBlocked && (h.validation.ok || h.phase === "unknown");
  // Any edit voids the Real review (the sheet drops its confirmation too).
  useEffect(() => {
    if (confirming && h.phase !== "confirming" && !busy) setConfirming(false);
  }, [confirming, h.phase, busy]);

  const cancelFirst = async () => {
    const todo = cancels > 0 ? longResting.filter((id) => !cancelled.current.has(id)) : [];
    if (todo.length === 0) return;
    const res = await orders.cancel(todo);
    orders.afterOrder();
    const failed = new Set((res.errors ?? []).map((e) => e.router_order_id));
    for (const id of todo) if (!failed.has(id)) cancelled.current.add(id);
    if (res.errors?.length) throw new Error(`Couldn't cancel the ${long.label} bids first: ${res.errors.map((e) => e.error).join(" · ")}. Nothing was bought.`);
  };

  const place = async (arm: boolean) => {
    if (!canPlace) return;
    setNote(null);
    const out = await s.submit({ arm, beforeSend: cancelFirst });
    if (out.status === "armed") {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    if (out.status === "placed") {
      const v = out.state.venue;
      const placed = placedLegs(out.result.raw, [
        {
          ref: { positionHash: short.positionHash, partnerId: v?.partnerId ?? venueId, partnerName: v?.name ?? venueName },
          label: `Get out · ${short.label}`,
          cents: out.figures ? Math.round(out.figures.maxPrice * 100) : null,
          amount: out.figures?.cost ?? 0,
        },
      ]);
      source.addLegs(entry.opportunityId, legRefsFrom(placed));
      orders.afterOrder();
      onPlaced(placed);
      return;
    }
    if (out.status === "error" && out.unknown) orders.afterOrder();
    const after = cancelled.current.size > 0 ? ` (${cancelled.current.size} resting bid${cancelled.current.size === 1 ? " was" : "s were"} already cancelled.)` : "";
    setNote(
      out.status === "moved"
        ? {
            tone: "moved",
            text: out.to ? `The price moved: it's ${costText(out.to, ccy)} now. Check it and place it again.${after}` : `Nothing quotes ${short.label} on ${venueName} right now. Nothing was placed.${after}`,
          }
        : { tone: "error", text: `${out.message}${after}` },
    );
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-[12px] text-dim">
          Buy
          <input
            inputMode="decimal"
            value={qtyText}
            onChange={(e) => setQty(e.target.value)}
            aria-label={`${short.label} contracts to buy`}
            className={`nums min-h-11 w-20 rounded-md border bg-bg px-2 text-right text-[13px] text-ink outline-none lg:min-h-8 ${qtyBad ? "border-down/60" : "border-border-2"}`}
          />
          {short.label}
          <span className="text-faint">
            (missing{" "}
            <button type="button" className="nums underline underline-offset-2 hover:text-ink" onClick={() => setQty(String(missing))}>
              {fmtContracts(need.contracts)}
            </button>
            )
          </span>
        </label>
        <label className="flex items-center gap-2 text-[12px] text-dim">
          Venue
          <select
            value={venueId}
            onChange={(e) => {
              const v = venues.find((x) => x.partner_id === e.target.value);
              h.select(e.target.value);
              if (h.orderType === "limit") h.set({ maxPrice: (defaultBidCents(v?.price) ?? 50) / 100 });
            }}
            className="min-h-11 rounded-md border border-border-2 bg-bg px-2 text-[13px] text-ink lg:min-h-8"
          >
            {venues.map((v) => (
              <option key={v.partner_id} value={v.partner_id}>
                {v.partner_name ?? v.partner_id} · {fmtPrice(v.price, v.american_odds, format)}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1.5" role="group" aria-label="Order type">
          <Pill active={type === "market"} onClick={() => h.set({ orderType: "market", maxPrice: null })}>
            Market
          </Pill>
          <Pill
            active={type === "limit"}
            onClick={() => limitOk && h.set({ orderType: "limit", maxPrice: cents / 100 })}
            title={limitOk ? "Rest at a chosen price" : `${venueName} doesn't take limit orders`}
            className={limitOk ? "" : "opacity-40"}
          >
            Limit
          </Pill>
        </div>
        {type === "limit" && <CentsStepper cents={cents} onChange={(n) => h.set({ maxPrice: clampCents(n) / 100 })} label="Hedge limit price" />}
      </div>

      {f ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border border-border/60 bg-bg p-3 text-[13px] sm:grid-cols-5">
          <Fact label="Side">{short.label}</Fact>
          <Fact label="Venue">{venueName}</Fact>
          <Fact label="Price">
            <span className="nums">
              {type === "market" ? `${fmtPrice(f.price, null, format)} (max ${fmtPrice(f.maxPrice, null, format)})` : `${fmtPrice(f.maxPrice, null, format)} limit`}
            </span>
          </Fact>
          <Fact label="Contracts">
            <span className="nums">{fmtContracts(f.contracts)}</span>
          </Fact>
          <Fact label="Cost">
            <span className="nums">{costText(f, ccy)}</span>
          </Fact>
        </dl>
      ) : (
        <div className="text-[12px] text-dim">{h.loading || s.checking ? "Checking the price…" : (issues ?? "Enter the contracts to buy.")}</div>
      )}

      {preview && f && (
        <div className="text-[13px]">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {entry.sides.map((sd, i) => (
              <span key={sd.positionHash}>
                If {sd.outcome}: <span className={`nums font-semibold ${toneOf(preview.payout[i])}`}>{signedMoney(preview.payout[i], ccy)}</span>
              </span>
            ))}
          </div>
          <p className={`mt-1 ${preview.breaksEven ? "text-up" : "text-down"}`}>
            {preview.breaksEven
              ? Math.abs(preview.max - preview.min) < 0.05
                ? `Locks in ${signedMoney(preview.min, ccy)} either way.`
                : `Locks in at least ${signedMoney(preview.min, ccy)} whichever side wins.`
              : `This can't break even at this price: the worst case is ${signedMoney(preview.min, ccy)}.`}
            {f.wouldRest ? " Only if it fills: a limit rests until it does." : ""} {f.exact ? "Fees included." : s.checking ? "Checking fees with OpenMarkets…" : "Fees are estimates (≈)."}
          </p>
        </div>
      )}
      {f && h.quote?.expectedFill && h.quote.expectedFill.unfilled > 0.005 && (
        <p className="text-[12px] text-amber">Only {money(h.quote.expectedFill.allocated, ccy)} of it can fill at this price right now.</p>
      )}

      {longResting.length > 0 && (
        <label className="flex min-h-11 items-center gap-2 text-[13px] text-ink lg:min-h-8">
          <input type="checkbox" checked={cancelLong} onChange={(e) => setCancelLong(e.target.checked)} className="h-4 w-4 accent-amber" />
          First cancel the {longResting.length} resting {long.label} bid{longResting.length === 1 ? "" : "s"} ({longResting.length === 1 ? "it'd" : "they'd"} keep adding exposure)
        </label>
      )}
      {shortResting > 0 && (
        <p className="text-[12px] text-dim">
          {shortResting} {short.label} bid{shortResting === 1 ? " is" : "s are"} still resting. If {shortResting === 1 ? "it fills" : "they fill"} after this, you'll be long {short.label} instead.
        </p>
      )}

      {realBlocked && (
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-amber">
          <span className="min-w-0 flex-1">Real orders need order placement access, which this session wasn't granted.</span>
          <button type="button" onClick={orders.unlockReal} className={`${btnBase} ${btnPrimary}`}>
            Unlock Real
          </button>
        </div>
      )}
      {issues && f && <p className="text-[12px] text-amber">{issues}</p>}
      {note && <p className={`text-[12px] ${note.tone === "moved" ? "font-medium text-amber" : "text-down"}`}>{note.text}</p>}

      {confirming && f ? (
        <ConfirmOrders
          lines={[{ key: "hedge", what: `Get out · ${short.label}`, venue: venueName, cents: Math.round(f.maxPrice * 100), amount: f.cost + f.fee, type, contracts: f.contracts }]}
          cancels={cancels}
          ccy={ccy}
          busy={busy}
          onBack={() => {
            h.cancelConfirm();
            setConfirming(false);
          }}
          onConfirm={() => void place(false)}
        />
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button type="button" onClick={onClose} className={`${btnBase} ${btnIdle}`}>
            Not now
          </button>
          <button type="button" disabled={!canPlace} onClick={() => void place(sheetMode === "real")} className={`${btnBase} ${btnPrimary} font-semibold`}>
            {s.step === "checking"
              ? "Checking…"
              : busy
                ? "Placing…"
                : h.phase === "unknown"
                  ? "Check order status"
                  : sheetMode === "real"
                    ? "Review real order"
                    : `Buy ${short.label}${f ? ` · ${money(f.cost + f.fee, ccy)}` : ""}`}
          </button>
        </div>
      )}
    </>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wide text-faint">{label}</dt>
      <dd className="mt-0.5 break-words text-ink">{children}</dd>
    </div>
  );
}
